import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemePreference } from '../theme';
import type { Rascunho } from '../lib/rascunho';
import type { PedidoDeTarefa } from '../lib/tarefas';
import { SEM_FILTROS, type FiltrosDaLista, type Periodo } from '../lib/filtrosDaLista';

export type Density = 'compact' | 'comfortable' | 'relaxed';
export type QuickFilter = 'all' | 'unread' | 'flagged' | 'attachment' | 'snoozed';
/**
 * Modulo do rail, como o seletor do Outlook: E-mail, Contatos, Anexos ou as
 * Configuracoes, que ocupam a tela inteira no lugar do e-mail (ADR 0047).
 */
export type Modulo = 'mail' | 'contacts' | 'attachments' | 'tasks' | 'admin' | 'settings';

/** Secoes das Configuracoes, na ordem da navegacao lateral. */
export type SecaoDeConfiguracao =
  | 'aparencia'
  | 'leitura'
  | 'atalhos'
  | 'identidades'
  | 'redacao'
  | 'respostas'
  | 'pastas'
  | 'regras'
  | 'ausencia'
  | 'seguranca'
  | 'hospedagem';

/** Secao 4.5: altura da linha por densidade. A lista virtualizada le daqui. */
export const ROW_HEIGHT: Record<Density, number> = {
  compact: 52,
  comfortable: 72,
  relaxed: 88,
};

/**
 * Pasta inteligente aberta (WYM-12): vista virtual por consulta, sem pasta fisica.
 * `accountId` nulo reune todas as caixas.
 */
export interface PastaInteligenteAberta {
  chave: string;
  nome: string;
  query: string;
  accountId: string | null;
}

export const LIST_MIN = 320;
export const LIST_MAX = 560;

interface UiState {
  // Persistido
  theme: ThemePreference;
  density: Density;
  sidebarCollapsed: boolean;
  listWidth: number;
  collapsedFolders: string[];

  // Efemero
  modulo: Modulo;
  accountId: string | null;
  folderId: string | null;
  mensagemId: string | null;
  checkedIds: string[];
  filter: QuickFilter;
  /** Filtros combinaveis do menu da lista; zeram ao trocar de conta, pasta ou pasta inteligente. */
  filtros: FiltrosDaLista;
  query: string;
  /** Texto da paleta: uma busca salva abre a paleta ja preenchida (ADR 0050). */
  buscaDaPaleta: string;
  /** Preferencia `atalhos.ativos`, espelhada aqui para o ouvinte de teclado ler sem hook. */
  atalhosAtivos: boolean;
  paletteOpen: boolean;
  helpOpen: boolean;
  /**
   * Secao aberta nas Configuracoes. `null` e a lista de secoes: em tela
   * estreita ela vem primeiro; na larga, cai na primeira secao.
   */
  settingsTab: SecaoDeConfiguracao | null;
  /** Para onde Esc e "Voltar" das Configuracoes levam. */
  moduloAnterior: Exclude<Modulo, 'settings'>;
  composerOpen: boolean;
  /**
   * Rascunho com que o composer abre. Responder e encaminhar entram por aqui;
   * `null` e mensagem nova, que retoma o rascunho guardado.
   */
  composerSemente: Rascunho | null;
  /** Rascunho do servidor que o composer abre (ADR 0052); `null` e mensagem nova. */
  composerRascunhoId: string | null;
  /** Lista de rascunhos aberta sem uma pasta Rascunhos da caixa por tras. */
  verRascunhos: boolean;
  pastaInteligente: PastaInteligenteAberta | null;
  /** Sobe a cada `v`: a lista abre o menu da conversa aberta (tem o Mover para). */
  pedidoDeMenu: number;
  pedidoDeTarefa: PedidoDeTarefa | null;
  pedidoDeRegra: { accountId: string; remetente: string } | null;

  setTheme: (theme: ThemePreference) => void;
  setDensity: (density: Density) => void;
  toggleSidebar: () => void;
  setListWidth: (width: number) => void;
  toggleFolder: (id: string) => void;

  setModulo: (modulo: Modulo) => void;
  selectAccount: (id: string) => void;
  selectFolder: (id: string) => void;
  selectMensagem: (id: string | null) => void;
  toggleChecked: (id: string) => void;
  setChecked: (ids: string[]) => void;
  clearChecked: () => void;
  setFilter: (filter: QuickFilter) => void;
  alternarFiltro: (chave: Exclude<keyof FiltrosDaLista, 'periodo'>) => void;
  setPeriodo: (periodo: Periodo | null) => void;
  limparFiltros: () => void;
  setQuery: (query: string) => void;
  setBuscaDaPaleta: (busca: string) => void;
  setAtalhosAtivos: (ativos: boolean) => void;
  /** Abre a paleta ja com a consulta; a conta, se a busca tiver uma, e escolhida antes. */
  abrirBuscaSalva: (query: string, accountId: string | null) => void;
  setPaletteOpen: (open: boolean) => void;
  setHelpOpen: (open: boolean) => void;
  setSettingsTab: (tab: SecaoDeConfiguracao | null) => void;
  /** Sem secao, abre na lista (estreita) ou na primeira secao (larga). */
  abrirConfiguracoes: (tab?: SecaoDeConfiguracao) => void;
  fecharConfiguracoes: () => void;
  setComposerOpen: (open: boolean) => void;
  abrirComposerCom: (semente: Rascunho) => void;
  abrirRascunho: (id: string) => void;
  abrirRascunhos: () => void;
  abrirPastaInteligente: (pasta: PastaInteligenteAberta) => void;
  /** Volta a lista para a pasta de antes (`folderId` segue guardado). */
  fecharPastaInteligente: () => void;
  pedirMenuDaConversa: () => void;
  /** Abre o dialogo de tarefa (WYM-16): criar a partir de uma mensagem ou editar. */
  pedirTarefa: (pedido: PedidoDeTarefa | null) => void;
  /** Abre o dialogo de regra ja preenchido com o remetente de uma mensagem. */
  pedirRegra: (pedido: { accountId: string; remetente: string } | null) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      density: 'comfortable',
      sidebarCollapsed: false,
      listWidth: 400,
      collapsedFolders: [],

      modulo: 'mail',
      accountId: null,
      folderId: null,
      mensagemId: null,
      checkedIds: [],
      filter: 'all',
      filtros: SEM_FILTROS,
      query: '',
      buscaDaPaleta: '',
      atalhosAtivos: true,
      paletteOpen: false,
      helpOpen: false,
      settingsTab: null,
      moduloAnterior: 'mail',
      composerOpen: false,
      composerSemente: null,
      composerRascunhoId: null,
      verRascunhos: false,
      pastaInteligente: null,
      pedidoDeMenu: 0,
      pedidoDeTarefa: null,
      pedidoDeRegra: null,

      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setListWidth: (width) => set({ listWidth: clamp(Math.round(width), LIST_MIN, LIST_MAX) }),
      toggleFolder: (id) =>
        set((s) => ({
          collapsedFolders: s.collapsedFolders.includes(id)
            ? s.collapsedFolders.filter((f) => f !== id)
            : [...s.collapsedFolders, id],
        })),

      setModulo: (modulo) => set({ modulo }),
      // Trocar de conta ou pasta zera selecao: o id selecionado nao existe do outro lado.
      selectAccount: (accountId) =>
        set({
          accountId,
          folderId: null,
          mensagemId: null,
          checkedIds: [],
          verRascunhos: false,
          pastaInteligente: null,
          filtros: SEM_FILTROS,
        }),
      selectFolder: (folderId) =>
        set({
          folderId,
          mensagemId: null,
          checkedIds: [],
          verRascunhos: false,
          pastaInteligente: null,
          filtros: SEM_FILTROS,
        }),
      selectMensagem: (mensagemId) => set({ mensagemId }),
      toggleChecked: (id) =>
        set((s) => ({
          checkedIds: s.checkedIds.includes(id)
            ? s.checkedIds.filter((c) => c !== id)
            : [...s.checkedIds, id],
        })),
      setChecked: (checkedIds) => set({ checkedIds }),
      clearChecked: () => set({ checkedIds: [] }),
      setFilter: (filter) => set({ filter }),
      alternarFiltro: (chave) =>
        set((s) => ({ filtros: { ...s.filtros, [chave]: !s.filtros[chave] } })),
      setPeriodo: (periodo) => set((s) => ({ filtros: { ...s.filtros, periodo } })),
      limparFiltros: () => set({ filtros: SEM_FILTROS }),
      setQuery: (query) => set({ query }),
      setBuscaDaPaleta: (buscaDaPaleta) => set({ buscaDaPaleta }),
      setAtalhosAtivos: (atalhosAtivos) => set({ atalhosAtivos }),
      abrirBuscaSalva: (query, accountId) =>
        set((s) => ({
          buscaDaPaleta: query,
          paletteOpen: true,
          ...(accountId !== null && accountId !== s.accountId
            ? { accountId, folderId: null, mensagemId: null, checkedIds: [] }
            : {}),
        })),
      setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
      setHelpOpen: (helpOpen) => set({ helpOpen }),
      setSettingsTab: (settingsTab) => set({ settingsTab }),
      abrirConfiguracoes: (settingsTab) =>
        set((s) => ({
          settingsTab: settingsTab ?? null,
          modulo: 'settings',
          // Abrir de dentro das Configuracoes nao pode apagar o caminho de volta.
          moduloAnterior: s.modulo === 'settings' ? s.moduloAnterior : s.modulo,
        })),
      fecharConfiguracoes: () => set((s) => ({ modulo: s.moduloAnterior })),
      // Fechar limpa a semente: a proxima abertura por "Escrever" nao pode
      // reaproveitar a resposta de antes.
      setComposerOpen: (composerOpen) =>
        set(
          composerOpen
            ? { composerOpen }
            : { composerOpen, composerSemente: null, composerRascunhoId: null },
        ),
      abrirComposerCom: (composerSemente) => set({ composerSemente, composerOpen: true }),
      abrirRascunho: (composerRascunhoId) =>
        set({ composerRascunhoId, composerSemente: null, composerOpen: true }),
      pedirMenuDaConversa: () => set((s) => ({ pedidoDeMenu: s.pedidoDeMenu + 1 })),
      pedirTarefa: (pedidoDeTarefa) => set({ pedidoDeTarefa }),
      pedirRegra: (pedidoDeRegra) => set({ pedidoDeRegra }),
      abrirRascunhos: () =>
        set({ verRascunhos: true, pastaInteligente: null, mensagemId: null, checkedIds: [] }),
      abrirPastaInteligente: (pastaInteligente) =>
        set({
          pastaInteligente,
          verRascunhos: false,
          mensagemId: null,
          checkedIds: [],
          filtros: SEM_FILTROS,
        }),
      fecharPastaInteligente: () =>
        set({ pastaInteligente: null, mensagemId: null, checkedIds: [] }),
    }),
    {
      name: 'waymail.ui',
      version: 1,
      // Selecao de mensagem e busca sao do momento, nao da preferencia. A caixa e
      // a pasta abertas ficam: recarregar a pagina nao pode trocar de caixa. O
      // que for lembrado e conferido contra o servidor antes de valer (AppShell).
      partialize: (s) => ({
        theme: s.theme,
        density: s.density,
        sidebarCollapsed: s.sidebarCollapsed,
        listWidth: s.listWidth,
        collapsedFolders: s.collapsedFolders,
        accountId: s.accountId,
        folderId: s.folderId,
        // Caixa sem pasta Rascunhos: sem isto o F5 volta para a Entrada (N8).
        verRascunhos: s.verRascunhos,
        // Pasta inteligente aberta sobrevive ao F5; se foi excluida, o AppShell a fecha.
        pastaInteligente: s.pastaInteligente,
      }),
    },
  ),
);
